/**
 * Table class สำหรับจัดการตาราง DataTable
 * @author เต้ย
 */
class ModuleTable {
  /**
   * สร้าง instance ของ Table
   * @param {Template} template - Template instance
   */
  constructor(template) {
    this.template = template;
  }
  /**
   * สร้างและกำหนดค่า DataTable
   * @param {Object} tableOption - ตัวเลือกสำหรับการสร้างตาราง
   * @param {string} tableOption.table - CSS selector ของตารางที่ต้องการใช้งาน
   * @param {boolean} [tableOption.stateSave=false] - บันทึกสถานะของตาราง (การเรียงลำดับ, การค้นหา, หน้าปัจจุบัน)
   * @param {number} [tableOption.iDisplayLength=10] - จำนวนรายการที่แสดงต่อหน้า
   * @param {boolean} [tableOption.ordering=false] - เปิดใช้งานการเรียงลำดับคอลัมน์
   * @param {Array} [tableOption.order] - ลำดับเริ่มต้นตาม DataTables · ส่ง [] เพื่อคงลำดับข้อมูลที่ใส่มา
   * @param {Object} [tableOption.select] - กำหนดการเลือกแถวในตาราง
   * @param {Object} [tableOption.ajax] - กำหนดการโหลดข้อมูลแบบ AJAX
   * @param {Array} tableOption.columns - กำหนดคอลัมน์ของตาราง
   * @param {Array} [tableOption.columnDefs] - กำหนดคุณสมบัติเพิ่มเติมของคอลัมน์
   * @param {Function} [tableOption.createdRow] - ฟังก์ชันที่ทำงานเมื่อสร้างแถวใหม่
   * @param {string} [tableOption.tableSearch] - CSS selector สำหรับช่องค้นหา
   * @param {Object} [tableOption.tableSearchDate] - กำหนดการค้นหาด้วยช่วงวันที่
   * @param {string} tableOption.tableSearchDate.start - CSS selector สำหรับวันที่เริ่มต้น
   * @param {string} tableOption.tableSearchDate.end - CSS selector สำหรับวันที่สิ้นสุด
   * @param {Array} [tableOption.tableSearchSelect] - รายการ CSS selector สำหรับ dropdown ที่ใช้ในการค้นหา
   * @returns {Object} - DataTable instance ที่สร้างขึ้น
   * @author เต้ย
   */
  initTable = (tableOption) => {
    const self = this;
    let table = null;
    let tableTimer = null;

    this._injectSkeletonStyles();
    this._injectSkeletonRows(tableOption);

    let options = {
      deferRender: true,
      searchDelay: tableOption.ajax ? 300 : undefined,
      processing: tableOption.ajax ? true : false,
      serverSide: tableOption.ajax ? true : false,
      stateSave: tableOption.stateSave ? true : false,
      iDisplayLength: tableOption.iDisplayLength ? tableOption.iDisplayLength : 10,
      ordering: tableOption.ordering ? tableOption.ordering : false,
      select: tableOption.select ? tableOption.select : undefined,
      language: {
        "lengthMenu": "แสดง _MENU_ รายการต่อหน้า",
        "zeroRecords": `<div class="text-center py-10">
            <i class="fas fa-inbox fs-3x text-muted mb-3 d-block"></i>
            <div class="fw-bold fs-5 text-gray-700 mb-1">ไม่พบข้อมูลที่ตรงกับเงื่อนไข</div>
            <div class="text-muted fs-7">ลองปรับ filter หรือล้างการค้นหา</div>
          </div>`,
        "info": "แสดงหน้า _PAGE_ จาก _PAGES_",
        "emptyTable": `<div class="text-center py-10">
            <i class="fas fa-inbox fs-3x text-muted mb-3 d-block"></i>
            <div class="fw-bold fs-5 text-gray-700">ไม่มีข้อมูลในตาราง</div>
          </div>`,
        "infoFiltered": "(กรองจากทั้งหมด _MAX_ รายการ)",
        "loadingRecords": "กำลังโหลดข้อมูล...",
        "infoEmpty": "แสดง 0 ถึง 0 จากทั้งหมด 0 รายการ",
        ...(tableOption.language || {}),
      },
      ajax: tableOption.ajax ? {
        ...tableOption.ajax,
        error: async function (xhr, error, thrown) {
          if (xhr.status === 401) {
            self.template._moduleToast.error('SESSION หมดอายุ กรุณาเข้าสู่ระบบใหม่อีกครั้ง');
            setTimeout(() => {
              location.href = `${self.template.baseUrl}welcome/errorPage/401`;
            }, 3000);
            return;
          }
          const msg = xhr.status === 0
            ? 'เชื่อมต่อ server ไม่ได้ กรุณาตรวจสอบอินเทอร์เน็ต'
            : (error || 'เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง');
          self.template._moduleToast.error(msg);
          self._renderTableError(tableOption, msg, () => table && table.ajax && table.ajax.reload());
        }
      } : undefined,
      columns: tableOption.columns,
      columnDefs: tableOption.columnDefs,
      createdRow: tableOption.createdRow ? tableOption.createdRow : function (row, data, dataIndex) { },
      drawCallback: function (settings) {
        let response = settings.json;
        if (typeof (response) !== "undefined") {
          $('.csrf-page').val(response.token);
          self.template.setCsrfToken(response.token);
        }

        if(tableOption.fullCallback){
          tableOption.fullCallback();
        }
      },
    };

    if (tableOption.deferLoading !== undefined) options.deferLoading = tableOption.deferLoading;
    if (tableOption.order !== undefined) options.order = tableOption.order;
    table = $(`${tableOption.table}`).DataTable(options);

    // เพิ่ม event listener สำหรับการ draw ตาราง
    let menuDrawTimer = null;
    table.on('draw', function () {
      clearTimeout(menuDrawTimer);
      menuDrawTimer = setTimeout(() => {
        KTMenu.createInstances();
        KTApp.createInstances();
      }, 50);
    });

    if (tableOption.ajax) {
      table.on('processing.dt', function (e, settings, processing) {
        const $wrapper = $(settings.nTable).closest('.dataTables_wrapper');
        $wrapper.toggleClass('dt-is-processing', !!processing);
      });
    }

    // จัดการการค้นหาด้วย input
    if (tableOption.tableSearch) {
      $(document).on('keyup', `${tableOption.tableSearch}`, function (e) {
        if (e.which === 13) {
          table.search(e.target.value).draw();
        }
        clearTimeout(tableTimer);

        tableTimer = setTimeout(function () {
          table.search(e.target.value).draw();
        }, 300);
      });
    }

    // จัดการปุ่มล้างการค้นหา
    $(document).on('click', '.btn-range-clear', function (e) {
      table.draw();
    });

    /**
     * ปุ่มรีโหลด
     * @author เต้ย
     */

    tableOption.tableReload = !tableOption.tableReload ? '#reload' : tableOption.tableReload;
    $(document).on('click', `${tableOption.tableReload}`, function (e) {
      table.draw();
    });

    // จัดการการค้นหาด้วยช่วงวันที่
    if (tableOption.tableSearchDate && tableOption.tableSearchDate.start && tableOption.tableSearchDate.end) {
      $(document).on('change', `${tableOption.tableSearchDate.start},${tableOption.tableSearchDate.end}`, function (e) {
        if ($(`${tableOption.tableSearchDate.start}`).val() != '' && $(`${tableOption.tableSearchDate.end}`).val() != '') {
          clearTimeout(tableTimer);

          tableTimer = setTimeout(function () {
            table.draw();
          }, 300);
        }
      });
    }

    // จัดการการค้นหาด้วย select
    if (tableOption.tableSearchSelect) {
      tableOption.tableSearchSelect.forEach(element => {
        $(document).on('change', element, function (e) {
          table.draw();
        });
      });
    }

    return table;
  }
  /**
   * สร้างปุ่มกิจกรรม
   * @param {Array} menuItems - รายการปุ่มกิจกรรม
   * @param {string} menuItems.id - ID ของปุ่ม
   * @param {string} menuItems.label - ข้อความของปุ่ม
   * @param {string} menuItems.class - คลาสของปุ่ม
   * @returns {string} - HTML ของปุ่มกิจกรรม
   * @author เต้ย
   */
  actionColumn = (menuItems) => {
    return `<a href="#" class="btn btn-light btn-active-light-primary btn-sm m-1" data-kt-menu-trigger="click" data-kt-menu-placement="bottom-end" data-kt-menu-flip="top-end">
                กิจกรรม
                <span class="svg-icon svg-icon-5 m-0">
                  <svg xmlns="http:www.w3.org/2000/svg" xmlns:xlink="http:www.w3.org/1999/xlink" width="24px" height="24px" viewBox="0 0 24 24" version="1.1">
                    <g stroke="none" stroke-width="1" fill="none" fill-rule="evenodd">
                      <polygon points="0 0 24 0 24 24 0 24"></polygon>
                      <path d="M6.70710678,15.7071068 C6.31658249,16.0976311 5.68341751,16.0976311 5.29289322,15.7071068 C4.90236893,15.3165825 4.90236893,14.6834175 5.29289322,14.2928932 L11.2928932,8.29289322 C11.6714722,7.91431428 12.2810586,7.90106866 12.6757246,8.26284586 L18.6757246,13.7628459 C19.0828436,14.1360383 19.1103465,14.7686056 18.7371541,15.1757246 C18.3639617,15.5828436 17.7313944,15.6103465 17.3242754,15.2371541 L12.0300757,10.3841378 L6.70710678,15.7071068 Z" fill="currentColor" fill-rule="nonzero" transform="translate(12.000003, 11.999999) rotate(-180.000000) translate(-12.000003, -11.999999)"></path>
                    </g>
                  </svg>
                </span>
              </a>
              <div class="menu menu-sub menu-sub-dropdown menu-column menu-rounded menu-gray-600 menu-state-bg-light-primary fw-bold fs-7 w-125px py-4" data-kt-menu="true">
                ${menuItems.map(menu => `<div class="menu-item px-3">
                  <a href="javascript:void(0);" class="text-start menu-link px-3 ${menu.class}" data-id="${menu.id}"
                  ${menu.dataAttributes ? menu.dataAttributes.map(attribute => { return `data-${attribute.name}="${attribute.value}"`; }).join('') : ''}
                  >
                    ${menu.attributes ? menu.attributes.map(attribute => { return `<div class="d-none ${attribute.name}">${typeof attribute.value === 'string' ? attribute.value : JSON.stringify(attribute.value)}</div>`; }).join('') : ''}
                    ${menu.label}
                  </a>
                </div>`).join('')}
              </div>`;
  }
  /**
   * สร้างคอลัมน์ตัวเลข
   * @param {number} data - ข้อมูลตัวเลข
   * @returns {string} - HTML ของคอลัมน์ตัวเลข
   * @author เต้ย
   */
  numberColumn = (data) => {
    return `<div class="text-center">${data}</div>`;
  }

  _injectSkeletonStyles = () => {
    if (document.getElementById('module-table-skeleton-style')) return;
    const style = document.createElement('style');
    style.id = 'module-table-skeleton-style';
    style.textContent = `
      @keyframes moduleTableShimmer { 0% { background-position: -600px 0; } 100% { background-position: 600px 0; } }
      tr.module-table-skeleton-row td { padding: 14px 12px !important; border-color: #f1f3f6 !important; }
      tr.module-table-skeleton-row .skeleton-bar { display:block; height:12px; border-radius:4px; margin: 0 auto; background: linear-gradient(90deg, #f1f3f6 0%, #e4e9f0 50%, #f1f3f6 100%); background-size: 1200px 100%; animation: moduleTableShimmer 1.4s linear infinite; }
      .dataTables_wrapper.dt-is-processing tbody { opacity: 0.45; transition: opacity 0.18s ease-out; pointer-events: none; }
      .dataTables_wrapper.dt-is-processing tbody tr { cursor: progress; }

      #table-ticket thead th { position: sticky; top: 0; z-index: 3; background: #fff; box-shadow: inset 0 -1px 0 #e4e6ef; }
      #table-ticket tbody tr { transition: background-color 0.15s ease-out; position: relative; }
      #table-ticket tbody tr:hover { background-color: #f8fafc !important; }
      #table-ticket tbody tr.ticket-row-priority-urgent td:first-child { box-shadow: inset 3px 0 0 #dc2626; }
      #table-ticket tbody tr.ticket-row-priority-high td:first-child { box-shadow: inset 3px 0 0 #f97316; }
      #table-ticket tbody tr.ticket-row-priority-medium td:first-child { box-shadow: inset 3px 0 0 #2563eb; }
      #table-ticket tbody tr.ticket-row-priority-low td:first-child { box-shadow: inset 3px 0 0 #94a3b8; }
      #table-ticket .badge { border-radius: 999px; padding: 0.35em 0.75em; font-weight: 600; letter-spacing: 0.01em; }

      @media (max-width: 640px) {
        #table-ticket thead { display: none; }
        #table-ticket tbody tr { display: block; border: 1px solid #e4e6ef; border-radius: 10px; margin: 0 0 12px 0; padding: 12px 14px; background: #fff; box-shadow: 0 1px 3px rgba(0,0,0,0.06); }
        #table-ticket tbody tr td { display: block; border: none !important; padding: 4px 0 !important; text-align: left !important; width: 100% !important; }
        #table-ticket tbody tr td:first-child { display: none; }
        #table-ticket tbody tr td:nth-child(2) { font-weight: 700; font-size: 1.05rem; margin-bottom: 6px; border-bottom: 1px dashed #f1f3f6 !important; padding-bottom: 8px !important; }
        #table-ticket tbody tr.ticket-row-priority-urgent { border-left: 4px solid #dc2626; }
        #table-ticket tbody tr.ticket-row-priority-high { border-left: 4px solid #f97316; }
        #table-ticket tbody tr.ticket-row-priority-medium { border-left: 4px solid #2563eb; }
        #table-ticket tbody tr.ticket-row-priority-low { border-left: 4px solid #94a3b8; }
        #table-ticket tbody tr.ticket-row-priority-urgent td:first-child,
        #table-ticket tbody tr.ticket-row-priority-high td:first-child,
        #table-ticket tbody tr.ticket-row-priority-medium td:first-child,
        #table-ticket tbody tr.ticket-row-priority-low td:first-child { box-shadow: none; }
      }
    `;
    document.head.appendChild(style);
  }

  _renderTableError = (tableOption, message, retryFn) => {
    if (!tableOption || !tableOption.table) return;
    const $table = $(tableOption.table);
    if (!$table.length) return;
    const colCount = Array.isArray(tableOption.columns) ? tableOption.columns.length : 1;
    const escapeHtml = this.template && this.template._moduleHelper && this.template._moduleHelper.escapeHtml
      ? this.template._moduleHelper.escapeHtml.bind(this.template._moduleHelper)
      : (s) => String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
    const $tbody = $table.find('tbody');
    $tbody.html(`<tr><td colspan="${colCount}" class="text-center py-10">
        <i class="fas fa-triangle-exclamation fs-3x text-danger mb-3 d-block"></i>
        <div class="fw-bold fs-5 text-gray-700 mb-2">โหลดข้อมูลไม่สำเร็จ</div>
        <div class="text-muted fs-7 mb-3">${escapeHtml(message)}</div>
        <button type="button" class="btn btn-sm btn-light-primary module-table-retry-btn">
          <i class="fas fa-rotate-right me-1"></i>ลองใหม่
        </button>
      </td></tr>`);
    $tbody.find('.module-table-retry-btn').off('click').on('click', () => {
      if (typeof retryFn === 'function') retryFn();
    });
  }

  _injectSkeletonRows = (tableOption) => {
    if (!tableOption || !tableOption.ajax || !tableOption.table) return;
    // deferLoading = ตารางไม่ ajax ตอน init จึงไม่มีสถานะ loading — ข้าม skeleton
    // กัน DataTables อ่าน skeleton row เป็น data แล้ว column render escape เป็น text
    if (tableOption.deferLoading !== undefined) return;
    const $table = $(tableOption.table);
    if (!$table.length) return;
    const colCount = Array.isArray(tableOption.columns) ? tableOption.columns.length : 0;
    if (!colCount) return;
    const $tbody = $table.find('tbody');
    if (!$tbody.length || $tbody.children().length) return;
    const skeletonRows = Number.isInteger(tableOption.skeletonRows) ? tableOption.skeletonRows : 10;
    if (skeletonRows <= 0) return;
    const widths = [70, 55, 80, 45, 60, 75, 50, 65];
    let rowsHtml = '';
    for (let r = 0; r < skeletonRows; r++) {
      let cells = '';
      for (let c = 0; c < colCount; c++) {
        const w = widths[(c + r) % widths.length];
        cells += `<td><span class="skeleton-bar" style="width:${w}%"></span></td>`;
      }
      rowsHtml += `<tr class="module-table-skeleton-row">${cells}</tr>`;
    }
    $tbody.html(rowsHtml);
  }
}